import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogIn, LogOut, Menu, X, User } from 'lucide-react';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { trackNavigationClick, logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';
import { NotificationBell, NotificationTray } from '@/shared/components/NotificationTray';
import { trackLicensing } from '@/features/licensing/public/analytics';

const Header: React.FC = () => {
  const { currentUser, userData, logOut } = useEnhancedAuth();
  const isLoggedIn = !!currentUser;
  const isSubscribed = userData?.isSubscribed ?? false;
  // "Pricing" was absent from the header entirely (only About/Blog), so the
  // marketing site had no navigable route to the offer. Shown to every visitor
  // EXCEPT people who already bought — `hasFullAccess` is the authoritative
  // backend entitlement flag.
  const showPricingNav = !userData?.hasFullAccess;
  const trackPricingNav = (surface: 'header_link' | 'mobile_menu') => {
    trackNavigationClick(surface, 'Pricing', '/pricing', isLoggedIn, location.pathname);
    logAnalyticsEvent(ANALYTICS_EVENTS.SUBSCRIPTION_CLICKED, {
      subscriptionStatus: userData?.subscriptionStatus ?? null,
      isLoggedIn,
      source: surface === 'header_link' ? 'header_nav_pricing' : 'mobile_menu_pricing',
      timestamp: Date.now(),
    });
  };
  // Licensing is a marketing surface for everyone, logged in or out: the state
  // pages answer the question people ask before they ever buy prep, and #418
  // shipped them with no route in from the site chrome.
  const trackLicensingNav = (surface: 'header_link' | 'mobile_menu') => {
    trackNavigationClick(surface, 'Licensing', '/licensing', isLoggedIn, location.pathname);
    trackLicensing('licensing_nav_clicked', {
      source: surface === 'header_link' ? 'header_nav_licensing' : 'mobile_menu_licensing',
      isLoggedIn,
    });
  };

  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quizInProgress, setQuizInProgress] = useState(false);
  const [currentQuizId, setCurrentQuizId] = useState<string | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const location = useLocation();

  // Track scroll for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const checkQuizStatus = useCallback(async () => {
    try {
      // const inProgress = await getActiveQuiz();
      // setQuizInProgress(inProgress.length !== 0);
      // setCurrentQuizId(inProgress[0].quizId);
    } catch (error) {
      console.error('Error checking quiz status:', error);
    }
  }, []);

  useEffect(() => {
    checkQuizStatus();

    const handleQuizProgressUpdate = () => {
      checkQuizStatus();
    };

    const handleQuizCompleted = () => {
      setQuizInProgress(false);
      setCurrentQuizId(null);
    };

    window.addEventListener('quizProgressUpdated', handleQuizProgressUpdate);
    window.addEventListener('storage', checkQuizStatus);
    window.addEventListener('quizDataCleared', checkQuizStatus);
    window.addEventListener('quizCompleted', handleQuizCompleted);

    return () => {
      window.removeEventListener('quizProgressUpdated', handleQuizProgressUpdate);
      window.removeEventListener('storage', checkQuizStatus);
      window.removeEventListener('quizDataCleared', checkQuizStatus);
      window.removeEventListener('quizCompleted', handleQuizCompleted);
    };
  }, [checkQuizStatus]);

  useEffect(() => {
    // Dark mode is PARKED for the .pen rollout — light-only warm system.
    // Restore when a dark ledge variant exists in the .pen.
    document.documentElement.classList.remove('dark');
  }, []);

  const handleSignInOut = async () => {
    if (isLoggedIn) {
      try {
        trackNavigationClick('header_link', 'Sign Out', '/', true, location.pathname);
        await logOut();
        navigate('/');
      } catch (error) {
        console.error('Error signing out:', error);
      }
    } else {
      const destination = isSubscribed ? '/dashboard' : '/signin';
      trackNavigationClick('header_link', 'Sign In', destination, false, location.pathname);
      navigate(destination);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleContinueQuiz = () => {
    if (currentQuizId) {
      trackNavigationClick('header_link', 'Continue Quiz', `/quiz/${currentQuizId}`, true, location.pathname);
      navigate(`/quiz/${currentQuizId}`);
    } else {
      trackNavigationClick('header_link', 'Continue Quiz Fallback', '/', true, location.pathname);
      navigate('/');
    }
    closeMobileMenu();
  };


  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <header className={`sticky top-0 z-50 bg-neutral-100 border-b border-neutral-200 transition-all duration-300 ${
      scrolled ? 'shadow-ledge' : ''
    }`}>
      <div className="container-base">
        <div className="flex justify-between items-center h-16">
          {/* Logo — stay on /try mid-diagnostic; never yank that funnel to / */}
          <Link
            to={isLoggedIn ? "/dashboard" : location.pathname === '/try' ? '/try' : '/'}
            onClick={() => {
              const destination = isLoggedIn ? '/dashboard' : location.pathname === '/try' ? '/try' : '/';
              trackNavigationClick('logo', 'Social Work Test Prep Academy', destination, isLoggedIn, location.pathname);
              closeMobileMenu();
            }}
            className="flex items-center group"
          >
            <span className="font-display text-xl sm:text-2xl font-semibold text-neutral-900 group-hover:text-primary-600 transition-colors">
              SWTPA
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 bg-transparent">
            {isLoggedIn && (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => trackNavigationClick('header_link', 'Dashboard', '/dashboard', true, location.pathname)}
                  className={`px-4 py-2 body-base font-medium rounded-lg transition-all duration-200 ${
                    isActivePath('/dashboard')
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 bg-transparent'
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/flashcards"
                  onClick={() => trackNavigationClick('header_link', 'Flashcards', '/flashcards', true, location.pathname)}
                  className={`px-4 py-2 body-base font-medium rounded-lg transition-all duration-200 ${
                    isActivePath('/flashcards')
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 bg-transparent'
                  }`}
                >
                  Flashcards
                </Link>
                <Link
                  to="/progress"
                  onClick={() => trackNavigationClick('header_link', 'Progress', '/progress', true, location.pathname)}
                  className={`px-4 py-2 body-base font-medium rounded-lg transition-all duration-200 ${
                    isActivePath('/progress')
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 bg-transparent'
                  }`}
                >
                  Progress
                </Link>
                <div className="h-4 w-px bg-neutral-200 mx-2"></div>
              </>
            )}

            <Link
              to="/about"
              onClick={() => trackNavigationClick('header_link', 'About Us', '/about', isLoggedIn, location.pathname)}
              className={`px-4 py-2 body-base font-medium rounded-lg transition-all duration-200 ${
                isActivePath('/about')
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 bg-transparent'
              }`}
            >
              About
            </Link>

            <Link
              to="/blog"
              onClick={() => trackNavigationClick('header_link', 'Blog', '/blog', isLoggedIn, location.pathname)}
              className={`px-4 py-2 body-base font-medium rounded-lg transition-all duration-200 ${
                isActivePath('/blog') || location.pathname.startsWith('/blog/')
                  ? 'text-[var(--color-primary-600)] dark:text-[var(--color-primary-400)] bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-900)]/20'
                  : 'text-[var(--color-neutral-700)] dark:text-[var(--color-neutral-600)] hover:text-[var(--color-neutral-900)] dark:hover:text-[var(--color-neutral-900)] hover:bg-[var(--color-neutral-50)] dark:hover:bg-[var(--color-neutral-200)] bg-transparent'
              }`}
            >
              Blog
            </Link>

            <Link
              to="/licensing"
              data-testid="header-licensing"
              onClick={() => trackLicensingNav('header_link')}
              className={`px-4 py-2 body-base font-medium rounded-lg transition-all duration-200 ${
                isActivePath('/licensing') || location.pathname.startsWith('/licensing/')
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 bg-transparent'
              }`}
            >
              Licensing
            </Link>

            {showPricingNav && (
              <Link
                to="/pricing"
                data-testid="header-pricing"
                onClick={() => trackPricingNav('header_link')}
                className={`px-4 py-2 body-base font-semibold rounded-lg transition-all duration-200 ${
                  isActivePath('/pricing')
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 bg-transparent'
                }`}
              >
                Pricing
              </Link>
            )}

            {isLoggedIn && (
              <>
                <div className="h-4 w-px bg-neutral-200 mx-2"></div>
                <Link
                  to="/settings"
                  onClick={() => trackNavigationClick('header_link', 'Settings', '/settings', true, location.pathname)}
                  className={`px-4 py-2 body-base font-medium rounded-lg transition-all duration-200 ${
                    isActivePath('/settings')
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 bg-transparent'
                  }`}
                >
                  Settings
                </Link>
                {quizInProgress && (
                  <button
                    onClick={handleContinueQuiz}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium transition-colors ml-2"
                  >
                    Continue Quiz
                  </button>
                )}
              </>
            )}

            <div className="flex items-center gap-3 ml-4 bg-transparent">
              {/* Notification Bell - only for logged in users */}
              {isLoggedIn && (
                <div className="relative">
                  <NotificationBell />
                  <NotificationTray />
                </div>
              )}

              {/* Dark-mode toggle hidden — dark mode is parked for the .pen rollout
                  (light-only warm system). Restore with the isDarkMode branch in
                  the class-toggle effect above when a dark ledge variant exists. */}

              {isLoggedIn ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    data-testid="user-menu"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-neutral-50 transition-all duration-200"
                  >
                    <User className="w-4 h-4 text-neutral-600" />
                    <span className="body-small font-medium text-neutral-700">
                      {userData?.profile?.name || 'Menu'}
                    </span>
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-neutral-100 rounded-xl shadow-ledge-lg border border-neutral-200 py-2 animate-fade-in">
                      <button
                        onClick={handleSignInOut}
                        data-testid="logout-button"
                        className="w-full text-left px-4 py-2.5 body-small text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/signin"
                  data-testid="login-button"
                  className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </Link>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg text-neutral-600 hover:bg-neutral-50 transition-all duration-200"
            onClick={toggleMobileMenu}
          >
            <span className="sr-only">Open menu</span>
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-neutral-200 animate-fade-in">
            <div className="flex flex-col gap-2">
              {isLoggedIn && (
                <>
                  <Link
                    to="/dashboard"
                    className={`px-4 py-3 rounded-lg body-base font-medium transition-all duration-200 ${
                      isActivePath('/dashboard')
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-neutral-700 hover:bg-neutral-50'
                    }`}
                    onClick={() => {
                      trackNavigationClick('mobile_menu', 'Dashboard', '/dashboard', true, location.pathname);
                      closeMobileMenu();
                    }}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/flashcards"
                    className={`px-4 py-3 rounded-lg body-base font-medium transition-all duration-200 ${
                      isActivePath('/flashcards')
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-neutral-700 hover:bg-neutral-50'
                    }`}
                    onClick={() => {
                      trackNavigationClick('mobile_menu', 'Flashcards', '/flashcards', true, location.pathname);
                      closeMobileMenu();
                    }}
                  >
                    Flashcards
                  </Link>
                  <Link
                    to="/progress"
                    className={`px-4 py-3 rounded-lg body-base font-medium transition-all duration-200 ${
                      isActivePath('/progress')
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-neutral-700 hover:bg-neutral-50'
                    }`}
                    onClick={() => {
                      trackNavigationClick('mobile_menu', 'Progress', '/progress', true, location.pathname);
                      closeMobileMenu();
                    }}
                  >
                    Progress
                  </Link>
                  <div className="h-px bg-neutral-200 my-2"></div>
                </>
              )}

              <Link
                to="/about"
                className={`px-4 py-3 rounded-lg body-base font-medium transition-all duration-200 ${
                  isActivePath('/about')
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-neutral-700 hover:bg-neutral-50'
                }`}
                onClick={() => {
                  trackNavigationClick('mobile_menu', 'About Us', '/about', isLoggedIn, location.pathname);
                  closeMobileMenu();
                }}
              >
                About
              </Link>

              <Link
                to="/blog"
                className={`px-4 py-3 rounded-lg body-base font-medium transition-all duration-200 ${
                  isActivePath('/blog') || location.pathname.startsWith('/blog/')
                    ? 'text-[var(--color-primary-600)] dark:text-[var(--color-primary-400)] bg-[var(--color-primary-50)] dark:bg-[var(--color-primary-900)]/20'
                    : 'text-[var(--color-neutral-700)] dark:text-[var(--color-neutral-600)] hover:bg-[var(--color-neutral-50)] dark:hover:bg-[var(--color-neutral-200)]'
                }`}
                onClick={() => {
                  trackNavigationClick('mobile_menu', 'Blog', '/blog', isLoggedIn, location.pathname);
                  closeMobileMenu();
                }}
              >
                Blog
              </Link>

              <Link
                to="/licensing"
                data-testid="mobile-licensing"
                className={`px-4 py-3 rounded-lg body-base font-medium transition-all duration-200 ${
                  isActivePath('/licensing') || location.pathname.startsWith('/licensing/')
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-neutral-700 hover:bg-neutral-50'
                }`}
                onClick={() => {
                  trackLicensingNav('mobile_menu');
                  closeMobileMenu();
                }}
              >
                Licensing
              </Link>

              {showPricingNav && (
                <Link
                  to="/pricing"
                  data-testid="mobile-pricing"
                  className={`px-4 py-3 rounded-lg body-base font-semibold transition-all duration-200 ${
                    isActivePath('/pricing')
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-neutral-700 hover:bg-neutral-50'
                  }`}
                  onClick={() => {
                    trackPricingNav('mobile_menu');
                    closeMobileMenu();
                  }}
                >
                  Pricing
                </Link>
              )}

              {isLoggedIn && (
                <>
                  <div className="h-px bg-neutral-200 my-2"></div>
                  <Link
                    to="/settings"
                    className={`px-4 py-3 rounded-lg body-base font-medium transition-all duration-200 ${
                      isActivePath('/settings')
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-neutral-700 hover:bg-neutral-50'
                    }`}
                    onClick={closeMobileMenu}
                  >
                    Settings
                  </Link>
                  {quizInProgress && (
                    <button
                      onClick={() => {
                        handleContinueQuiz();
                        closeMobileMenu();
                      }}
                      className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium transition-colors w-full mt-2"
                    >
                      Continue Quiz
                    </button>
                  )}
                </>
              )}

              <div className="h-px bg-neutral-200 my-2"></div>

              {/* Dark-mode toggle hidden — dark mode parked for the .pen rollout. */}

              {!isLoggedIn && (
                <Link
                  to="/try"
                  onClick={() => {
                    trackNavigationClick('mobile_menu', 'Get 7 free days', '/try', false, location.pathname);
                    closeMobileMenu();
                  }}
                  data-testid="mobile-signup"
                  className="btn-primary w-full"
                >
                  Get 7 free days
                </Link>
              )}

              <button
                onClick={() => {
                  handleSignInOut();
                  closeMobileMenu();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg body-base font-medium text-neutral-700 hover:bg-neutral-50 transition-all duration-200"
              >
                {isLoggedIn ? (
                  <>
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Sign In
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
